import { Check, Clock, Mail, Send, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { RoommateShort } from 'types/api'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { usePatchApplication } from '@services/applications/mutations/usePatchApplication'
import { useRemoveRoommate } from '@services/applications/mutations/useRemoveRoommate'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const STATUS_META = {
  PENDING: { tone: 'bg-peach/25 text-warning', Icon: Clock },
  ACCEPTED: { tone: 'bg-primary/10 text-primary', Icon: Check },
  REJECTED: { tone: 'bg-destructive/10 text-destructive', Icon: X }
} as const

interface Props {
  roommates?: RoommateShort[]
  applicationId?: string
  disabled?: boolean
  /** Admin-only: link each roommate's name to their user profile. */
  linkToProfile?: boolean
}

export function RoommateManager({
  roommates = [],
  applicationId,
  disabled,
  linkToProfile
}: Props) {
  const { t } = useTranslation('application')
  const patch = usePatchApplication()
  const remove = useRemoveRoommate()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  const invite = () => {
    const value = email.trim().toLowerCase()
    if (!EMAIL_RE.test(value)) {
      setError(t('form.sections.roommates.invalidEmail'))
      return
    }
    if (roommates.some((r) => r.email?.toLowerCase() === value)) {
      setError(t('form.sections.roommates.alreadyInvited'))
      return
    }
    setError(null)
    patch.mutate(
      { id: applicationId, body: { roommates: [{ email: value }] } },
      { onSuccess: () => setEmail('') }
    )
  }

  return (
    <div className="box-border space-y-4 [&_*]:box-border">
      {!disabled && (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">
            {t('form.sections.roommates.roommateEmail')}
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setError(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    invite()
                  }
                }}
                placeholder={t(
                  'form.sections.roommates.roommateEmailPlaceholder'
                )}
                className="pl-10"
              />
            </div>
            <Button
              type="button"
              onClick={invite}
              disabled={!email.trim() || !applicationId || patch.isPending}
            >
              <Send className="h-4 w-4" />
              {t('form.sections.roommates.sendInvite')}
            </Button>
          </div>
          {error && (
            <p className="mt-1.5 text-sm font-medium text-destructive">
              {error}
            </p>
          )}
        </div>
      )}

      {roommates.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-ink/15 px-4 py-8 text-center text-sm text-muted-foreground">
          {t('form.sections.roommates.empty')}
        </div>
      ) : (
        <ul className="m-0 w-full list-none space-y-2.5 p-0">
          {roommates.map((r) => {
            const status = r.invitation
              ?.status as keyof typeof STATUS_META | undefined
            const meta = status ? STATUS_META[status] : undefined
            const name =
              [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email
            const initials = (
              r.first_name?.[0] ??
              r.email?.[0] ??
              '?'
            ).toUpperCase()
            return (
              <li
                key={r.invitation?.id ?? r.email}
                className="flex w-full items-center gap-3 rounded-2xl border border-ink/[0.06] bg-white p-3.5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                  {initials}
                </span>
                <div className="min-w-0 flex-1">
                  {linkToProfile && r.id ? (
                    <Link
                      to={`/admin/users/${r.id}`}
                      className="block truncate text-sm font-semibold text-primary hover:underline"
                    >
                      {name}
                    </Link>
                  ) : (
                    <div className="truncate text-sm font-semibold text-ink">
                      {name}
                    </div>
                  )}
                  {name !== r.email && (
                    <div className="truncate text-xs text-muted-foreground">
                      {r.email}
                    </div>
                  )}
                </div>
                {meta && status && (
                  <span
                    className={cn(
                      'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold',
                      meta.tone
                    )}
                  >
                    <meta.Icon className="h-3.5 w-3.5" />
                    {t(`form.common.status.${status}`)}
                  </span>
                )}
                {!disabled && r.invitation?.id && (
                  <button
                    type="button"
                    onClick={() => remove.mutate({ id: r.invitation!.id! })}
                    aria-label={t('form.sections.roommates.remove')}
                    title={t('form.sections.roommates.remove')}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-0 p-0 bg-destructive text-white shadow-sm transition-colors hover:bg-destructive-strong"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M3 6h18" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" x2="10" y1="11" y2="17" />
                      <line x1="14" x2="14" y1="11" y2="17" />
                    </svg>
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
